/* eslint-disable react-hooks/exhaustive-deps */
import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useEffect, useState } from "react";
import {
  editComplaint,
  fetchAdminUnitDetail,
  fetchAssignedProsecutor,
  fetchChildAdminUnits,
} from "services/advocateComplaintService";
import { AdminUnit, AssignedProsecutor, Complaints, Prosecutor } from "types";
import {
  COMPLIANT_STATUS,
  DECISION_TYPE,
  DECISION_TYPE2,
  HIERARCHY_LEVELS,
} from "utils/constantValues";
import { useTranslation } from "contexts/i18nContext";
import "../../complaintRequest/styles/AddEvidenceModal.css";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: Complaints;
  onRefresh: any;
}

export default function AddComplaintDecisionModal({
  isOpen,
  setIsOpen,
  complaintData,
  onRefresh,
}: ModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [prosecutorsData, setProsecutorsData] = useState<AssignedProsecutor[]>(
    [],
  );
  const [adminUnitData, setAdminUnitData] = useState<AdminUnit>();
  const [childAdminUnit, setChildAdminUnit] = useState<any>([]);

  const [formState, setFormState] = useState({
    decision_type: DECISION_TYPE?.APPROVED,
    decision_summary: "",
    decision_date: new Date(),
    assigned_date: new Date().toISOString().split("T")[0],
    assigned_prosecutor_id: "",
    admin_unit_id: "",
    assigned_prosecutor: null as Prosecutor | null,
    complaint: null,
    files: [] as File[],
  });

  useEffect(() => {
    const loadProsecutors = async () => {
      try {
        const data = await fetchAssignedProsecutor(
          user?.admin_unit,
          user?.org_unit,
          user?.access,
        );
        setProsecutorsData(data);
      } catch (err) {
        console.error("Failed to load prosecutors:", err);
      } finally {
        setLoading(false);
      }
    };
    loadProsecutors();
  }, [user]);

  useEffect(() => {
    const loadAdminUnitDetail = async () => {
      try {
        const adminUnitDetail = await fetchAdminUnitDetail(
          user?.admin_unit,
          user?.access,
        );
        setAdminUnitData(adminUnitDetail);
      } catch (err) {
        console.error("Failed to load admin unit detail:", err);
      } finally {
        setLoading(false);
      }
    };
    loadAdminUnitDetail();
  }, [user]);

  useEffect(() => {
    const loadChildAdminUnit = async () => {
      try {
        const adminUnitDetail = await fetchChildAdminUnits(
          adminUnitData?.uu_id || "",
          user?.access,
        );
        setChildAdminUnit(adminUnitDetail?.units);
      } catch (err) {
        console.error("Failed to load child admin units:", err);
      } finally {
        setLoading(false);
      }
    };
    adminUnitData?.uu_id && loadChildAdminUnit();
  }, [adminUnitData]);

  const filtered = prosecutorsData
    ?.filter((item) =>
      (item.roles as string[]).includes("AdvocateDisciplineProsecutor"),
    )
    ?.filter((p) =>
      `${p?.user?.first_name} ${p?.user?.last_name}`
        .toLowerCase()
        .includes(query.toLowerCase()),
    );

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "decision_type") {
      setFormState((prev) => ({ ...prev, assigned_prosecutor: null }));
    }

    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (prosecutor: Prosecutor, name: string) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: prosecutor,
    }));
    setQuery("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      formState?.decision_type === DECISION_TYPE?.APPROVED &&
      !formState?.assigned_prosecutor
    ) {
      showToast(t("prosecutorRequired"), "error");
      return;
    }

    setLoading(true);

    try {
      await editComplaint({
        ...complaintData,
        ...formState,
        admin_unit_id:
          formState?.decision_type === DECISION_TYPE.JURISDICTION &&
          adminUnitData?.hierarchy_level?.name === HIERARCHY_LEVELS.REGIONAL
            ? formState?.admin_unit_id
            : formState?.decision_type === DECISION_TYPE.JURISDICTION &&
                adminUnitData?.hierarchy_level?.name === HIERARCHY_LEVELS.ZONAL
              ? adminUnitData?.parent_unit || complaintData?.admin_unit_id
              : complaintData?.admin_unit_id,
        userid_assigned_prosecutor: formState?.assigned_prosecutor
          ? formState?.assigned_prosecutor?.id
          : "",
        assigned_prosecutor: `${
          formState?.assigned_prosecutor?.first_name || ""
        } ${formState?.assigned_prosecutor?.last_name || ""}`,
        status:
          formState?.decision_type === DECISION_TYPE?.APPROVED
            ? COMPLIANT_STATUS?.UNDER_INVESTIGATION
            : formState?.decision_type === DECISION_TYPE2.MORE_INFORMATION
              ? COMPLIANT_STATUS.FURTHER_INVESTIGATION
              : formState?.decision_type === DECISION_TYPE2.JURISDICTION
                ? COMPLIANT_STATUS.FORWARDED
                : COMPLIANT_STATUS?.CLOSED,
      });

      onRefresh();
      setIsOpen(false);
      showToast(t("decisionCreated"), "success");
      setFormState({
        decision_type: DECISION_TYPE?.APPROVED,
        decision_summary: "",
        admin_unit_id: "",
        decision_date: new Date(),
        assigned_prosecutor_id: "",
        assigned_prosecutor: null,
        complaint: null,
        assigned_date: new Date().toISOString().split("T")[0],
        files: [] as File[],
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(
        err?.response?.data?.message || t("somethingWentWrong"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("addDecision")}>
      <div className="p-1">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Decision Type Section */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-red-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  {t("decisionType")}
                </div>
              </label>
              <select
                required
                className="w-full border border-gray-300 bg-white rounded-xl p-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                name="decision_type"
                value={formState.decision_type}
                onChange={handleChange}
              >
                {Object.values(DECISION_TYPE2)?.map((list) => (
                  <option key={list} value={list}>
                    {list}
                  </option>
                ))}
              </select>
            </div>

            {/* Jurisdiction Zone Selection */}
            {formState?.decision_type === DECISION_TYPE.JURISDICTION &&
              adminUnitData?.hierarchy_level?.name ===
                HIERARCHY_LEVELS?.REGIONAL && (
                <div className="transform transition-all duration-300 ease-in-out">
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-red-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      {t("forwardToZone")}
                    </div>
                  </label>
                  <select
                    required
                    className="w-full border border-gray-300 bg-white rounded-xl p-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                    name="admin_unit_id"
                    value={formState.admin_unit_id}
                    onChange={handleChange}
                  >
                    <option value="">{t("selectZone")}</option>
                    {childAdminUnit?.map((list: any) => (
                      <option key={list.uu_id} value={list.uu_id}>
                        {list?.unit_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {/* Decision Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-red-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  {t("decisionDate")}
                </div>
              </label>
              <input
                required
                className="w-full border border-gray-300 bg-white rounded-xl p-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                type={"date"}
                name="assigned_date"
                value={formState.assigned_date}
                onChange={handleChange}
              />
            </div>

            {/* Decision Summary */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-red-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                    />
                  </svg>
                  {t("decisionSummary")}
                </div>
              </label>
              <textarea
                rows={4}
                name="decision_summary"
                value={formState.decision_summary}
                onChange={handleChange}
                placeholder={t("decisionSummaryPlaceholder")}
                className="w-full border border-gray-300 bg-white rounded-xl p-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 resize-none"
              />
              {/* <p className="text-xs text-gray-500 mt-1">Provide a clear summary of the decision</p> */}
            </div>

            {/* Assign Prosecutor (Only for Approved Decisions) */}
            {formState?.decision_type === DECISION_TYPE?.APPROVED && (
              <div className="transform transition-all duration-300 ease-in-out">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-red-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-2.646a9 9 0 01-13.5 2.646"
                      />
                    </svg>
                    {t("assignProsecutor")}
                  </div>
                </label>
                <select
                  required
                  className="w-full border border-gray-300 bg-white rounded-xl p-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  name="assigned_prosecutor_id"
                  value={formState.assigned_prosecutor_id}
                  onChange={(e) => {
                    handleChange(e);
                    const selected = filtered?.find(
                      (p) => p.user.id === e.target.value,
                    );
                    if (selected && selected.user) {
                      handleSelect(selected.user, "assigned_prosecutor");
                    }
                  }}
                >
                  <option value="">{t("selectProsecutor")}</option>
                  {filtered?.map((p) => (
                    <option key={p.user.id} value={p.user.id}>
                      {p.user?.first_name} {p.user?.last_name} • {p.user?.email}
                    </option>
                  ))}
                </select>
                {formState.assigned_prosecutor && (
                  <div className="mt-2 p-3 bg-gradient-to-r from-red-50 to-white rounded-lg border border-red-100">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-800 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {formState.assigned_prosecutor.first_name?.charAt(0)}
                          {formState.assigned_prosecutor.last_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {formState.assigned_prosecutor.first_name}{" "}
                          {formState.assigned_prosecutor.last_name}
                        </p>
                        <p className="text-xs text-gray-600">
                          {formState.assigned_prosecutor.email}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Buttons inline */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-red-800 to-red-900 text-white font-semibold py-3 rounded-xl hover:from-red-900 hover:to-red-950 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:transform-none"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t("processing")}
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {formState?.decision_type === DECISION_TYPE.JURISDICTION &&
                  adminUnitData?.hierarchy_level?.name ===
                    HIERARCHY_LEVELS?.REGIONAL
                    ? t("forwardToSelectedZone")
                    : formState?.decision_type === DECISION_TYPE.JURISDICTION &&
                        adminUnitData?.hierarchy_level?.name ===
                          HIERARCHY_LEVELS?.ZONAL
                      ? t("forwardToRegional")
                      : t("submitDecision")}
                </div>
              )}
            </button>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-xl hover:bg-gray-50 transition-all duration-200"
            >
              {t("cancel")}
            </button>
          </div>
        </form>
      </div>
    </SideModal>
  );
}
